import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import {
  ProgrammeAction,
  ProgrammeIcon,
} from '../components/programmes/ProgrammeUI';
import { getProgramme } from '../data/programmeJourneys';
import '../styles/pages/ProgrammeJourneys.css';

const ProgrammeLandingPage = ({
  afterContent = null,
  kind,
}) => {
  const programme = getProgramme(kind);
  const reduceMotion = useReducedMotion();

  return (
    <PageWrapper>
      <SEO
        description={programme.seoDescription}
        keywords={`${programme.title}, Supernova Lab, application, programme, portal`}
        title={`${programme.title} | Supernova Lab`}
      />

      <main
        className={`pj-public pj-theme--${programme.accent}`}
        data-programme={programme.key}
      >
        <section className="pj-public-hero" aria-labelledby="programme-title">
          <div className="pj-public-shell">
            <motion.div
              className="pj-public-hero__content"
              initial={reduceMotion ? false : { opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            >
              <h1 id="programme-title">{programme.title}</h1>
              <h2>{programme.tagline}</h2>
              <p>
                {programme.description}
                {programme.descriptionSecondary
                  ? ` ${programme.descriptionSecondary}`
                  : ''}
              </p>
              <div className="pj-public-actions">
                <ProgrammeAction to={`/${programme.slug}/apply`}>
                  {programme.applyLabel}
                </ProgrammeAction>
                <ProgrammeAction
                  to={`/${programme.slug}/login`}
                  variant="secondary"
                >
                  {programme.loginLabel}
                </ProgrammeAction>
              </div>
            </motion.div>
          </div>
        </section>

        <section
          className="pj-process-band"
          aria-labelledby={`${programme.key}-process-title`}
        >
          <div className="pj-public-shell pj-process-band__layout">
            <div className="pj-process-intro">
              <h2 id={`${programme.key}-process-title`}>
                {programme.publicSectionTitle}
              </h2>
              <p>{programme.publicSectionCopy}</p>
            </div>

            <ol className="pj-process-steps">
              {programme.publicSteps.map((step) => (
                <li key={step.title}>
                  <div className="pj-process-step__top">
                    <span className="pj-process-step__icon" aria-hidden="true">
                      <ProgrammeIcon name={step.icon} size={29} />
                    </span>
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </li>
              ))}
            </ol>

            <p className="pj-process-summary">
              {programme.publicPoints.join('. ')}. After submitting, applicants
              can create portal access to track their application and receive
              updates. Onboarding and participant resources become available
              only after approval or selection.
            </p>
          </div>
        </section>

        <section
          className="pj-programme-path"
          aria-labelledby={`${programme.key}-journey-title`}
        >
          <div className="pj-public-shell">
            <div className="pj-section-heading">
              <h2 id={`${programme.key}-journey-title`}>
                {programme.secondaryTitle}
              </h2>
              <p>{programme.secondaryCopy}</p>
            </div>

            <ol className="pj-theme-rail">
              {programme.themes.map((theme) => (
                <li key={theme.title}>
                  <span aria-hidden="true">
                    <ProgrammeIcon name={theme.icon} size={26} />
                  </span>
                  <strong>{theme.title}</strong>
                </li>
              ))}
            </ol>

            <div className="pj-public-closing">
              <div>
                <h2>
                  {programme.key === 'zone'
                    ? 'Ready to test the idea?'
                    : 'Ready to build with a cohort?'}
                </h2>
                <p>
                  Review the application questions and prepare the information
                  you will need.
                </p>
              </div>
              <ProgrammeAction to={`/${programme.slug}/apply`}>
                {programme.applyLabel}
              </ProgrammeAction>
            </div>
          </div>
        </section>

        {afterContent}
      </main>
    </PageWrapper>
  );
};

export default ProgrammeLandingPage;
